import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowSquareOut, Copy, Check, DownloadSimple, Sparkle, Spinner, ChatCircleText,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import Cookies from "js-cookie";

// ── Portal metadata ─────────────────────────────────────────────────────────
// Each entry: text-key in pack, label, color, deep-link generator (city-aware)
const PORTAL_DEFS = [
  {
    key: "idealista",
    label: "Idealista",
    accent: "#00B4A1",
    insertUrl: () => "https://www.idealista.it/pro/inserisci-annuncio",
  },
  {
    key: "immobiliare",
    label: "Immobiliare.it",
    accent: "#F2353C",
    insertUrl: () => "https://www.immobiliare.it/pro/inserisci-annuncio/",
  },
  {
    key: "email",
    label: "Email",
    accent: "#7C8B6E",
    insertUrl: (prop) =>
      `mailto:?subject=${encodeURIComponent(prop.title || "Proposta immobile")}&body=`,
    isMail: true,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    accent: "#25D366",
    insertUrl: () => "https://wa.me/",
    isWhatsapp: true,
  },
];

const STEPS = ["foto", "testo", "apri"];

export default function PublishNowDialog({
  open, onOpenChange,
  property, propertyId, photos, pack,
  packLoading, onGeneratePack,
}) {
  const [active, setActive] = useState("idealista");
  const [done, setDone] = useState(/** @type {Record<string, Record<string, boolean>>} */ ({}));

  useEffect(() => {
    if (!open) return;
    if (!pack && !packLoading) onGeneratePack?.();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentText = useMemo(() => {
    if (!pack) return "";
    const v = pack[active];
    return typeof v === "string" ? v : "";
  }, [pack, active]);

  const enhancedCount = (photos || []).filter(
    (p) => (p.photo_kind || "photo") === "photo" && p.enhanced_path,
  ).length;

  const markStep = (portalKey, step) => {
    setDone((d) => ({
      ...d,
      [portalKey]: { ...(d[portalKey] || {}), [step]: true },
    }));
  };

  const onDownloadPhotos = () => {
    if (!propertyId) return;
    const token = Cookies.get("vista_jwt");
    const qs = new URLSearchParams({ kind: "enhanced" });
    if (token) qs.append("auth", token);
    window.location.href = `${API}/properties/${propertyId}/photos/zip?${qs.toString()}`;
    markStep(active, "foto");
    toast.success("Download foto avviato — pronte da caricare");
  };

  const onCopyText = async () => {
    if (!currentText) return;
    try {
      await navigator.clipboard.writeText(currentText);
      markStep(active, "testo");
      toast.success("Testo copiato negli appunti");
    } catch {
      toast.error("Browser non permette la copia automatica — selezionalo manualmente");
    }
  };

  const onOpenPortal = () => {
    const def = PORTAL_DEFS.find((p) => p.key === active);
    if (!def) return;
    const url = def.insertUrl(property || {});
    window.open(url, "_blank", "noopener,noreferrer");
    markStep(active, "apri");
    if (def.isMail || def.isWhatsapp) {
      toast.message("Incolla il testo nel messaggio");
    } else {
      toast.message("Incolla il testo nel form del portale");
    }
  };

  const portalDone = (k) => {
    const d = done[k] || {};
    return STEPS.every((s) => d[s]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0"
        data-testid="publish-now-dialog"
      >
        <div className="p-8 lg:p-10 border-b border-[#E5E7E0]">
          <DialogHeader>
            <span className="overline">Pubblica in 60 secondi</span>
            <DialogTitle className="font-editorial text-3xl mt-2 leading-tight">
              Pubblica subito sul portale che preferisci
            </DialogTitle>
            <DialogDescription className="text-[#6B6A65] text-base mt-2">
              Foto già pronte ({enhancedCount} migliorate), testo ottimizzato per ogni portale,
              link diretto al form di pubblicazione. Niente ti rallenta.
            </DialogDescription>
          </DialogHeader>
        </div>

        {packLoading && (
          <div className="p-12 flex flex-col items-center gap-3 text-[#6B6A65]" data-testid="publish-pack-loading">
            <Spinner className="animate-spin" size={28} />
            <p className="text-sm">Sto preparando i testi ottimizzati per ogni portale…</p>
          </div>
        )}

        {!pack && !packLoading && (
          <div className="p-10 text-center" data-testid="publish-pack-empty">
            <p className="text-[#6B6A65] text-sm mb-4">
              Per pubblicare ho bisogno di generare i testi ottimizzati per ogni portale.
            </p>
            <button onClick={onGeneratePack} className="btn-primary text-sm" data-testid="publish-pack-generate">
              <Sparkle size={16} weight="fill" /> Genera i testi
            </button>
          </div>
        )}

        {pack && (
          <>
            {/* Portal tabs */}
            <div className="flex flex-wrap gap-2 px-8 lg:px-10 pt-6 pb-3" data-testid="publish-portal-tabs">
              {PORTAL_DEFS.map((p) => {
                const isActive = active === p.key;
                const completed = portalDone(p.key);
                return (
                  <button
                    key={p.key}
                    onClick={() => setActive(p.key)}
                    data-testid={`publish-tab-${p.key}`}
                    className={`px-4 py-2 text-sm transition-colors border ${
                      isActive
                        ? "bg-[#7C8B6E] text-[#FFFFFF] border-[#7C8B6E]"
                        : "bg-transparent text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"
                    }`}
                  >
                    {completed && <Check size={12} weight="bold" className="inline mr-1" />}
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Active portal pane */}
            <div className="px-8 lg:px-10 pb-10 pt-3 space-y-5" data-testid={`publish-pane-${active}`}>
              {/* Text preview */}
              <div className="border border-[#E5E7E0] bg-[#FFFFFF]">
                <div className="px-5 py-3 border-b border-[#E5E7E0] flex items-center justify-between">
                  <span className="overline">Anteprima testo · {PORTAL_DEFS.find((p) => p.key === active)?.label}</span>
                  <span className="text-xs text-[#9A9892]">{currentText.length} caratteri</span>
                </div>
                <pre
                  className="px-5 py-4 text-sm text-[#1C1C1A] whitespace-pre-wrap font-sans leading-relaxed max-h-72 overflow-y-auto"
                  data-testid={`publish-text-${active}`}
                >
                  {currentText || <span className="text-[#9A9892] italic">Testo non disponibile per questo portale.</span>}
                </pre>
              </div>

              {/* Tags (visible on portal tabs only) */}
              {Array.isArray(pack.tags) && pack.tags.length > 0 && active !== "email" && active !== "whatsapp" && (
                <div className="flex flex-wrap gap-2" data-testid="publish-tags">
                  {pack.tags.map((t, i) => (
                    <span key={i} className="text-xs px-2 py-1 border border-[#E5E7E0] text-[#6B6A65]">
                      #{t.replace(/^#/, "")}
                    </span>
                  ))}
                </div>
              )}

              {/* Step-by-step CTAs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="publish-steps">
                <StepButton
                  num={1}
                  done={done[active]?.foto}
                  icon={<DownloadSimple size={18} weight="regular" />}
                  label="Scarica foto (ZIP)"
                  hint={`${enhancedCount} foto migliorate`}
                  onClick={onDownloadPhotos}
                  disabled={enhancedCount === 0}
                  testId="publish-step-photos"
                />
                <StepButton
                  num={2}
                  done={done[active]?.testo}
                  icon={<Copy size={18} weight="regular" />}
                  label="Copia il testo"
                  hint="Negli appunti"
                  onClick={onCopyText}
                  disabled={!currentText}
                  testId="publish-step-copy"
                />
                <StepButton
                  num={3}
                  done={done[active]?.apri}
                  icon={
                    active === "whatsapp" ? <ChatCircleText size={18} weight="regular" /> :
                    <ArrowSquareOut size={18} weight="regular" />
                  }
                  label={
                    active === "email" ? "Apri Mail" :
                    active === "whatsapp" ? "Apri WhatsApp" :
                    "Apri portale"
                  }
                  hint="Incolla e pubblica"
                  onClick={onOpenPortal}
                  testId="publish-step-open"
                />
              </div>

              {portalDone(active) && (
                <div className="border-l-2 border-[#7C8B6E] bg-[#FFFFFF] px-5 py-4 text-sm flex items-center gap-3" data-testid="publish-done-banner">
                  <Check size={20} weight="bold" className="text-[#7C8B6E]" />
                  <div>
                    <div className="font-medium text-[#7C8B6E]">Annuncio pronto su {PORTAL_DEFS.find((p) => p.key === active)?.label}</div>
                    <div className="text-[#6B6A65] text-xs">Carica le foto, incolla il testo e pubblica. Buone visite.</div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StepButton({ num, done, icon, label, hint, onClick, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`text-left p-4 border transition-all relative ${
        done
          ? "bg-[#7C8B6E] text-[#FFFFFF] border-[#7C8B6E]"
          : disabled
          ? "border-[#E5E7E0] text-[#9A9892] cursor-not-allowed"
          : "border-[#E5E7E0] text-[#1C1C1A] hover:border-[#7C8B6E] hover:-translate-y-0.5"
      }`}
    >
      <div className={`text-xs mb-2 ${done ? "text-[#FFFFFF]/70" : "text-[#9A9892]"}`}>
        {done ? <Check size={14} weight="bold" className="inline" /> : `Passo ${num}`}
      </div>
      <div className="flex items-start gap-2">
        {icon}
        <div className="flex-1">
          <div className="font-medium text-sm leading-tight">{label}</div>
          <div className={`text-xs mt-1 ${done ? "text-[#FFFFFF]/70" : "text-[#9A9892]"}`}>{hint}</div>
        </div>
      </div>
    </button>
  );
}
